import {
  ExclamationTriangleIcon,
  ArrowUpIcon,
} from '@heroicons/react/24/outline';
import {
  getUsagePercentage,
  getUsageColorClass,
  formatEmployeeLimit,
  isUnlimitedTier,
} from '@/types/subscription';

interface LimitIndicatorProps {
  currentCount: number;
  limit: number;
  showUpgrade?: boolean;
  onUpgradeClick?: () => void;
  size?: 'sm' | 'md';
  isLoading?: boolean;
  className?: string;
}

/**
 * Compact inline indicator showing "3/10 employees"
 * Different from UsageMeter (which is a full card component)
 */
export default function LimitIndicator({
  currentCount,
  limit,
  showUpgrade = true,
  onUpgradeClick,
  size = 'md',
  isLoading = false,
  className = '',
}: LimitIndicatorProps) {
  // Loading skeleton
  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="w-16 h-4 bg-gray-200 animate-pulse rounded" />
        <div className="w-12 h-1.5 bg-gray-200 animate-pulse rounded-full" />
      </div>
    );
  }

  const unlimited = isUnlimitedTier(limit);
  const percentage = getUsagePercentage(currentCount, limit);
  const colors = getUsageColorClass(percentage);
  const isAtLimit = !unlimited && currentCount >= limit;
  const isNearLimit = !unlimited && percentage >= 80;

  const sizeClasses = {
    sm: 'text-xs gap-1.5',
    md: 'text-sm gap-2',
  };

  return (
    <div className={`flex items-center ${sizeClasses[size]} ${className}`}>
      {/* Count display */}
      <span className="font-medium text-gray-900">
        {currentCount}
        <span className="text-gray-500 mx-0.5">/</span>
        <span className={unlimited ? 'text-green-600' : 'text-gray-500'}>
          {formatEmployeeLimit(limit)}
        </span>
      </span>

      {/* Warning icon */}
      {isNearLimit && (
        <ExclamationTriangleIcon
          className={`h-4 w-4 ${isAtLimit ? 'text-red-500' : 'text-yellow-500'}`}
        />
      )}

      {/* Compact progress bar */}
      {!unlimited && (
        <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${colors.bg}`}
            style={{ width: `${Math.min(100, percentage)}%` }}
          />
        </div>
      )}

      {/* Upgrade button */}
      {showUpgrade && isNearLimit && onUpgradeClick && (
        <button
          onClick={onUpgradeClick}
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-medium text-indigo-600 hover:text-indigo-700"
        >
          <ArrowUpIcon className="h-3 w-3" />
          Upgrade
        </button>
      )}

      {/* Unlimited badge */}
      {unlimited && (
        <span className="px-1.5 py-0.5 text-xs font-medium text-green-700 bg-green-100 rounded">
          ∞
        </span>
      )}
    </div>
  );
}
